import { useState } from 'react';

export default function ProductInfo() {
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [activeTab, setActiveTab] = useState<'desc' | 'spec' | 'review'>('desc');

  const handleQuantityChange = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(10, prev + delta)));
  };

  const handleInquiry = () => {
    const contactSection = document.getElementById('product-inquiry');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.warn('Element with id "product-inquiry" not found.');
    }
  };

  const handleOrder = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div className="flex flex-col">
      {/* Badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-md whitespace-nowrap">
          BEST
        </span>
        <span className="bg-teal-600 text-white text-xs font-bold px-2.5 py-1 rounded-md whitespace-nowrap">
          정품
        </span>
        <span className="bg-gray-800 text-white text-xs font-bold px-2.5 py-1 rounded-md whitespace-nowrap">
          무료배송
        </span>
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
        삼성전자 990 PRO 2TB (MZ-V9P2T0BW)
      </h1>
      <p className="text-base text-gray-500 mb-4">
        M.2 NVMe SSD | PCIe Gen4 x4 | 최대 7,450MB/s
      </p>

      {/* Rating */}
      <div className="flex items-center gap-2 mb-6">
        <div className="flex items-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <i key={star} className="ri-star-fill text-yellow-400 text-lg"></i>
          ))}
        </div>
        <span className="text-sm text-gray-600 font-medium">4.9</span>
        <span className="text-sm text-gray-400">(128개 리뷰)</span>
      </div>

      {/* Price */}
      <div className="bg-gray-50 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-center">
          <span className="text-3xl font-bold text-teal-600">구매협의</span>
        </div>
        <p className="text-center text-sm text-gray-500 mt-2">
          견적 문의를 통해 최적의 가격을 안내해 드립니다
        </p>
      </div>

      {/* Key Specs */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
            <i className="ri-speed-line text-teal-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-5">읽기 속도</p>
            <p className="text-sm font-semibold text-gray-900">7,450 MB/s</p>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
            <i className="ri-edit-line text-teal-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">쓰기 속도</p>
            <p className="text-sm font-semibold text-gray-900">6,900 MB/s</p>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
            <i className="ri-database-2-line text-teal-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">용량</p>
            <p className="text-sm font-semibold text-gray-900">2TB</p>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
            <i className="ri-cpu-line text-teal-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">인터페이스</p>
            <p className="text-sm font-semibold text-gray-900">PCIe 4.0 x4</p>
          </div>
        </div>
      </div>

      {/* Delivery Info */}
      <div className="border border-gray-200 rounded-xl p-4 mb-6 space-y-3">
        <div className="flex items-center gap-3">
          <i className="ri-truck-line text-teal-600 text-lg"></i>
          <div>
            <span className="text-sm font-medium text-gray-900">무료배송</span>
            <span className="text-xs text-gray-500 ml-2">내일 도착 예정</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <i className="ri-shield-check-line text-teal-600 text-lg"></i>
          <div>
            <span className="text-sm font-medium text-gray-900">삼성전자 정품</span>
            <span className="text-xs text-gray-500 ml-2">5년 제조사 보증</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <i className="ri-exchange-line text-teal-600 text-lg"></i>
          <div>
            <span className="text-sm font-medium text-gray-900">교환/반품</span>
            <span className="text-xs text-gray-500 ml-2">수령 후 7일 이내</span>
          </div>
        </div>
      </div>

      {/* Quantity */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm font-medium text-gray-700">수량</span>
        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => handleQuantityChange(-1)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="w-12 h-10 flex items-center justify-center text-sm font-semibold text-gray-900 border-x border-gray-300">
            {quantity}
          </span>
          <button
            onClick={() => handleQuantityChange(1)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleInquiry}
          className="flex-1 border-2 border-teal-600 text-teal-600 py-3.5 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          견적 문의
        </button>
        <button
          onClick={handleOrder}
          className="flex-1 bg-teal-600 text-white py-3.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          주문하기
        </button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-xl z-50 flex items-center gap-2 animate-fade-in">
          <i className="ri-check-line text-teal-400"></i>
          <span className="text-sm">
            주문 문의가 접수되었습니다. 담당자가 곧 연락드리겠습니다.
          </span>
        </div>
      )}
    </div>
  );
}
